import React, { useEffect, useState } from 'react';
import { BUTTON_VARIANTS } from 'ui/button';
import { Close16 } from 'ui/icons/action/Close16';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { Truncate } from 'ui/utility/truncate';
import { TAG_SIZES, TagSize } from '../constants';
import { InlineTooltipContent, StyledIconButton, TagPrefix, TagWrapper } from '../Tags.styles';

export type TagProps = {
  /** Text to display in the tag */
  text?: string;
  /** Custom content in tags, only used for special tags exported in this file */
  children?: React.ReactNode;
  /** Size of the tag */
  size?: TagSize;
  /** Prefix to display before the tag content */
  prefix?: React.ReactNode;
  /** Max width of the tag */
  maxWidth?: number;
  /**
   * Disable the hover box shadow effect.
   */
  noBoxShadow?: boolean;
  /**
   * Enable visual interaction cues (cursor pointer, hover states).
   */
  hasInteraction?: boolean;
  /** True if tag is removable and will display an x icon to indicate this */
  isRemovable?: boolean;
  /**
   * Enable click interactions on the entire tag.
   */
  isClickable?: boolean;
  /**
   * Callback function called when the tag is clicked.
   * Only triggered when `isClickable` is `true`.
   */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Callback function called when the remove button is clicked.
   * Only triggered when `isRemovable` is `true`.
   */
  onRemove?: (text?: string) => void;
};

export const Tag = React.forwardRef(
  (
    {
      text,
      prefix,
      children,
      size = TAG_SIZES.SMALL,
      isRemovable = false,
      noBoxShadow = false,
      hasInteraction = false,
      isClickable = false,
      maxWidth,
      onRemove,
      onClick,
      ...props
    }: TagProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const [tagContentEl, setTagContent] = useState<HTMLElement | null>(null);
    const [shouldTruncate, setShouldTruncate] = useState(false);
    const tagContentMaxWidth = maxWidth ?? (size === TAG_SIZES.SMALL ? 100 : 150);

    useEffect(() => {
      if (tagContentEl && (text || children)) {
        const isOverflowing = tagContentEl.scrollWidth > tagContentEl.offsetWidth;
        setShouldTruncate(isOverflowing);
      }
    }, [tagContentEl, text, children]);

    const handleRemove = () => onRemove?.(text);
    const handleInteraction = (e: React.SyntheticEvent) => {
      // Prevent mouse events from bubbling up to react-select
      e.preventDefault();
      e.stopPropagation();
    };

    return (
      <TagWrapper
        data-test="tag"
        ref={ref}
        data-is-clickable={isClickable}
        data-has-box-shadow={!noBoxShadow}
        data-support-interaction={hasInteraction}
        data-size={size}
        data-truncated={shouldTruncate}
        onMouseDown={handleInteraction}
        onPointerDown={handleInteraction}
        onTouchStart={handleInteraction}
        onClick={(e) => {
          if (isClickable && onClick) {
            onClick(e);
          }
          if (!isClickable) {
            handleInteraction(e);
          }
        }}
        {...props}
      >
        <Tooltip
          automaticallyAvoidCollisions
          isEnabled={shouldTruncate}
          tooltipContent={text || children}
        >
          <InlineTooltipContent data-test="tag-content">
            {prefix && <TagPrefix>{prefix}</TagPrefix>}
            <Truncate ref={setTagContent} maxWidth={tagContentMaxWidth} data-test="tag-truncate">
              {text || children}
            </Truncate>
            {isRemovable && (
              <StyledIconButton
                variant={BUTTON_VARIANTS.GHOST}
                data-test={`${text}-tag-remove-button`}
                onClick={handleRemove}
                onTouchStart={handleRemove}
              >
                <Close16 />
              </StyledIconButton>
            )}
          </InlineTooltipContent>
        </Tooltip>
      </TagWrapper>
    );
  },
);
